/**
 * Template name normalization and formatting utilities
 * 
 * Handles:
 * - Collapsing repeated "(Recovered)" → "(Recovered vN)"
 * - Collapsing repeated "Copy" patterns → "Copy vN"
 * - Truncation for display
 * - Fallback to "Untitled Template"
 */

/**
 * Normalizes a template name for storage in the database.
 * - Collapses repeated suffixes
 * - Enforces database length limit (120 chars)
 * - Provides fallback for empty names
 * 
 * @param name - Raw template name
 * @returns Normalized name safe for database storage
 */
export function normalizeTemplateName(name: string): string {
  if (!name?.trim()) {
    return 'Untitled Template';
  }

  let normalized = name.trim();

  // Process in defined order: Recovered → Copy
  // This ensures consistent behavior when both patterns exist
  
  // Collapse repeated "(Recovered)" patterns
  normalized = normalized.replace(
    /\(Recovered\)(?:\s*\(Recovered\))+/gi,
    (match) => {
      const count = (match.match(/\(Recovered\)/gi) || []).length;
      return count > 1 ? `(Recovered v${count})` : '(Recovered)';
    }
  );

  // Collapse repeated "Copy" patterns (handles "Copy", "Copy of", "Copy ver2", etc.)
  normalized = normalized.replace(
    /Copy(?: of)?(?: v\d+)?(?: ver\d+)?(?:\s*Copy(?: of)?(?: v\d+)?(?: ver\d+)?)+/gi,
    (match) => {
      const count = (match.match(/Copy(?: of)?(?: v\d+)?(?: ver\d+)?/gi) || []).length;
      return count > 1 ? `Copy v${count}` : 'Copy';
    }
  );

  // Database limit (TEXT column, but we enforce 120 chars for consistency)
  return normalized.slice(0, 120);
}

/**
 * Formats a template name for display in the UI.
 * - First normalizes the name (collapses suffixes)
 * - Then truncates to maxLength with ellipsis
 * - Provides fallback for empty names
 * 
 * @param name - Raw template name
 * @param options - Formatting options
 * @param options.maxLength - Maximum display length (default: 72)
 * @returns Formatted name for UI display
 */
export function formatTemplateName(
  name: string,
  options: { maxLength?: number } = {}
): string {
  const { maxLength = 72 } = options;

  // First normalize (in case not already normalized)
  const normalized = normalizeTemplateName(name);

  // Then truncate for display
  if (normalized.length > maxLength) {
    return `${normalized.slice(0, maxLength - 1)}…`;
  }

  return normalized;
}

/**
 * Generates a new name for a copied template.
 * Applies normalization to prevent accumulation of "Copy" suffixes.
 * 
 * @param originalName - Name of the template being copied
 * @returns Normalized name for the copy
 */
export function generateCopyName(originalName: string): string {
  const base = originalName?.trim() || 'Untitled Template';
  return normalizeTemplateName(`${base} Copy`);
}

/**
 * Generates a new name for a recovered template.
 * Applies normalization to prevent accumulation of "(Recovered)" suffixes.
 * 
 * @param originalName - Name of the template being recovered
 * @returns Normalized name for the recovery
 */
export function generateRecoveredName(originalName: string): string {
  const base = originalName?.trim() || 'Recovered Template';
  // If it already ends with "(Recovered)", normalize will handle versioning
  const candidate = base.endsWith('(Recovered)') ? base : `${base} (Recovered)`;
  return normalizeTemplateName(candidate);
}

